import React, { useState, useEffect, useCallback } from 'react';
import { InputDataPicker, DataPickerContainer, Label, DateInputIcon, IconContainer, InputContainer } from '../DatePicker/DataPicker.styles';
import { ErrorMessage } from '../TextInput/TextInput.styled';
import { formatInput, monthsBetweenDateAndToday } from '@/utils/handleDate';
import { createValidationSchema } from '@/utils/handleDate';

export interface DateEmploymentPickerProps {
  dataTestId?: string;
  propertyName: 'startDate' | 'endDate' | 'startDateCoBorrower' | 'endDateCoBorrower';
  value?: string;
  placeholder?: string;
  label?: string;
  onChange?: (option: string, propertyType: string | undefined, index?: number) => void;
  propertyType?: string | undefined;
  isSoftPull?: boolean;
  typeOfIcon?: string;
  iconColor?: string;
  size?: string;
  isOccupancyDate?: boolean;
  labelContainer?: boolean;
  hasAsterisk?: boolean;
  selectedOption?: string;
  setEmploymentStartDate?: React.Dispatch<React.SetStateAction<number>>;
  setEmploymentEndDate?: React.Dispatch<React.SetStateAction<number>>;
  isHidden: boolean;
  isDisabled?: boolean;
  index?: number;
  hasLessThanTwoYears?: boolean;
  startDateValue?: string; // end date vs start date validation
  onValidationChange?: (isValid: boolean, index?: number) => void; // report validation state
}

// utility functions for date validation
const isValidDate = (dateString: string): boolean => {
  if (!dateString || !/^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(dateString)) {
    return false;
  }

  const [month, day, year] = dateString.split('/').map(Number);
  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

const isDateInFuture = (dateString: string): boolean => {
  if (!isValidDate(dateString)) return false;

  const [month, day, year] = dateString.split('/').map(Number);
  const inputDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return inputDate > today;
};

const isEndDateAfterStartDate = (startDate: string, endDate: string): boolean => {
  if (!isValidDate(startDate) || !isValidDate(endDate)) return false;

  const [startMonth, startDay, startYear] = startDate.split('/').map(Number);
  const [endMonth, endDay, endYear] = endDate.split('/').map(Number);

  const start = new Date(startYear, startMonth - 1, startDay);
  const end = new Date(endYear, endMonth - 1, endDay);

  return end > start;
};

export const DateEmploymentPicker: React.FC<DateEmploymentPickerProps> = ({
  dataTestId,
  propertyType,
  propertyName,
  value = '',
  placeholder = '',
  onChange,
  typeOfIcon,
  iconColor = '#5140E9',
  size = '18rem',
  labelContainer = false,
  label = '',
  hasAsterisk = false,
  selectedOption = 'false',
  setEmploymentStartDate,
  setEmploymentEndDate,
  isHidden,
  isDisabled = false,
  index,
  startDateValue = '',
  onValidationChange,
}) => {
  const [formattedValue, setFormattedValue] = useState(value || '');
  const [validationDateMessage, setValidationDateMessage] = useState('');
  const [invalidDate, setInvalidDate] = useState(false);
  const [maxLength, setMaxLength] = useState<number | undefined>(undefined);
  const isRequired = true;
  const formSchema = createValidationSchema(propertyName, isRequired);
  const isEmploymentStartDate = propertyName === 'startDate';
  const hasEmployedEndDate = propertyName === 'endDate';
  const isEmploymentCoBorrowerStartDate = propertyName === 'startDateCoBorrower';
  const hasEmployedCoBorrowerEndDate = propertyName === 'endDateCoBorrower';
  const isSoftPull = !isEmploymentStartDate && !hasEmployedEndDate && !isEmploymentCoBorrowerStartDate && !hasEmployedCoBorrowerEndDate;
  const [startEmploymentDate, setStartEmploymentDate] = useState<number>(0);
  const [totalEmployment, setTotalEmployment] = useState<number>(0);
  const [startEmploymentCoBorrowerDate, setStartEmploymentCoBorrowerDate] = useState<number>(0);
  const [totalCoBorrowerEmployment, setTotalCoBorrowerEmployment] = useState<number>(0);

  useEffect(() => {
    isEmploymentStartDate || hasEmployedEndDate ? dateToEmployment() : dateToEmploymentCoBorrower();
    if (selectedOption === 'true') {
      if (setEmploymentEndDate) setEmploymentEndDate(0);
    }
  }, [selectedOption, startEmploymentDate, totalEmployment, totalCoBorrowerEmployment, startEmploymentCoBorrowerDate]);

  const validateDateField = (dateValue: string): { isValid: boolean; message: string } => {
    if (!dateValue || dateValue.length < 10) {
      return { isValid: false, message: 'Date is required' };
    }

    // check for valid date format and validatedate is real
    if (!isValidDate(dateValue)) {
      return { isValid: false, message: 'You must provide a valid date' };
    }

    const year = parseInt(dateValue.split('/')[2], 10);
    if (year < 1900) {
      return { isValid: false, message: 'Year must be 1900 or later' };
    }

    // check if end date is in the future
    if ((hasEmployedEndDate || hasEmployedCoBorrowerEndDate) && isDateInFuture(dateValue)) {
      return { isValid: false, message: 'You must provide a valid date' };
    }

    // check if end date is after start date
    if ((hasEmployedEndDate || hasEmployedCoBorrowerEndDate) && startDateValue) {
      if (isValidDate(startDateValue) && !isEndDateAfterStartDate(startDateValue, dateValue)) {
        return { isValid: false, message: 'You must provide a valid date that is after your start date' };
      }
    }

    return { isValid: true, message: '' };
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMaxLength(10);
    const formatted = formatInput(event.target.value, propertyName);
    setFormattedValue(formatted);

    if (isEmploymentStartDate) dateToEmployment();

    // validate the date using comprehensive validation
    const validation = validateDateField(formatted);
    setValidationDateMessage(validation.message);
    setInvalidDate(!validation.isValid);

    // report validation state to parent
    if (onValidationChange) {
      onValidationChange(validation.isValid, index);
    }

    if (onChange) onChange(event.target.value, propertyType, index);
  };

  const dateToEmploymentCoBorrower = useCallback(() => {
    let monthsDateAndToday = monthsBetweenDateAndToday(formattedValue, propertyName);
    let endDate = 0;
    if (propertyType === 'startDateCoBorrower') {
      let startDateEmployed = startEmploymentCoBorrowerDate && !Number.isNaN(startEmploymentCoBorrowerDate) ? startEmploymentCoBorrowerDate : monthsDateAndToday;

      startDateEmployed = monthsDateAndToday;
      if (setEmploymentStartDate) setEmploymentStartDate(startDateEmployed);
      setStartEmploymentCoBorrowerDate(startDateEmployed);
    }
    if (propertyType === 'endDateCoBorrower') {
      const endDateEmployed = monthsDateAndToday;
      endDate = endDateEmployed;
    }
    const hasMinTimeAtWork = startEmploymentCoBorrowerDate >= endDate && startEmploymentCoBorrowerDate >= 48;
    setTotalCoBorrowerEmployment(startEmploymentCoBorrowerDate - endDate);
    if (setEmploymentEndDate) setEmploymentEndDate(endDate);
    return hasMinTimeAtWork;
  }, [formattedValue, propertyName, propertyType, startEmploymentCoBorrowerDate, setTotalEmployment, setEmploymentStartDate, setEmploymentEndDate]);

  const dateToEmployment = useCallback(() => {
    let monthsDateAndToday = monthsBetweenDateAndToday(formattedValue, propertyName);
    let endDate = 0;
    if (propertyType === 'startDate') {
      let startDateEmployed = startEmploymentDate && !Number.isNaN(startEmploymentDate) ? startEmploymentDate : monthsDateAndToday;

      startDateEmployed = monthsDateAndToday;
      if (setEmploymentStartDate) setEmploymentStartDate(startDateEmployed);
      setStartEmploymentDate(startDateEmployed);
    }
    if (propertyType === 'endDate') {
      const endDateEmployed = monthsDateAndToday;
      endDate = endDateEmployed;
    }
    const hasMinTimeAtWork = startEmploymentDate >= endDate && startEmploymentDate >= 24;
    setTotalEmployment(startEmploymentDate - endDate);
    if (setEmploymentEndDate) setEmploymentEndDate(endDate);
    return hasMinTimeAtWork;
  }, [formattedValue, propertyName, propertyType, startEmploymentDate, setTotalEmployment, setEmploymentStartDate, setEmploymentEndDate]);

  const onBlur = async () => {
    try {
      const validation = validateDateField(formattedValue);
      setValidationDateMessage(validation.message);
      setInvalidDate(!validation.isValid);

      // report validation state to parent
      if (onValidationChange) {
        onValidationChange(validation.isValid, index);
      }

      await formSchema.validate({ [propertyName]: formattedValue });
    } catch (error) {
      console.error(error);
      if (!validationDateMessage) {
        setValidationDateMessage('Date is required');
        setInvalidDate(true);
        if (onValidationChange) {
          onValidationChange(false, index);
        }
      }
    }
  };

  useEffect(() => {
    if (isDisabled) {
      setInvalidDate(false);
      setFormattedValue('');
      if (onValidationChange) {
        onValidationChange(true, index);
      }
    }
  }, [isDisabled]);

  // re-validate when startDateValue changes (for end date validation)
  useEffect(() => {
    if ((hasEmployedEndDate || hasEmployedCoBorrowerEndDate) && formattedValue && startDateValue) {
      const validation = validateDateField(formattedValue);
      setValidationDateMessage(validation.message);
      setInvalidDate(!validation.isValid);

      if (onValidationChange) {
        onValidationChange(validation.isValid, index);
      }
    }
  }, [startDateValue, formattedValue, hasEmployedEndDate, hasEmployedCoBorrowerEndDate]);

  return (
    <DataPickerContainer size={size} $isHidden={isHidden} $isDisabled={isDisabled}>
      {labelContainer && (
        <Label>
          {label}
          {hasAsterisk && <p>*</p>}
        </Label>
      )}
      <InputContainer $isError={invalidDate}>
        {typeOfIcon && (
          <IconContainer>
            <DateInputIcon $iconColor={iconColor} />
          </IconContainer>
        )}
        <InputDataPicker
          data-testid={dataTestId}
          placeholder={placeholder}
          onFocus={() => setInvalidDate(false)}
          onBlur={onBlur}
          onChange={handleChange}
          value={formattedValue}
          $isSoftPull={isSoftPull}
          size={size}
          maxLength={maxLength}
          disabled={isDisabled}
        />
      </InputContainer>

      {invalidDate && <ErrorMessage>{validationDateMessage}</ErrorMessage>}
    </DataPickerContainer>
  );
};

export default DateEmploymentPicker;
